import { FC, useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import {
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Radio,
    RadioGroup,
    SimpleGrid,
    VStack,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { AppFilterContainer } from "../../../containers";
import {
    AppFormControl,
    AppIcon,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { APPC } from "../../../config";
import {
    User as Entity,
    UserApi as EntityApi,
    SchoolBatch,
    SchoolBatchApi,
} from "../../../apis";
import { useAuthData } from "../../../contexts";

export type StudentFilterDataType = {
    user_search: string;
    studentTrainer: string;
    nationality: string;
    schoolBatch: string;
    status: string;
    isActive: boolean;
};

const defaultValues: StudentFilterDataType = {
    user_search: "",
    studentTrainer: "",
    nationality: "",
    schoolBatch: "",
    status: "",
    isActive: false,
};

export const StudentFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();
    const { grant } = useAuthData();

    // state & const
    const USERTYPE = APPC.BE.User.USERTYPE;
    const [isInit, setIsInit] = useState(false);
    const [loadingStudentTrainers, setLoadingStudentTrainers] = useState(true);
    const [studentTrainerOptions, setStudentTrainerOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedStudentTrainer, setSelectedStudentTrainer] =
        useState<AppSelectOption | null>(null);
    const [selectedNationality, setSelectedNationality] =
        useState<AppSelectOption | null>(
            filterData?.nationality
                ? {
                      value: filterData?.nationality,
                      label: filterData?.nationality,
                  }
                : null
        );
    const [selectedStatus, setSelectedStatus] =
        useState<AppSelectOption | null>(
            filterData?.status
                ? {
                      value: filterData?.status,
                      label: filterData?.status,
                  }
                : null
        );
    const [loadingSchoolBatch, setLoadingSchoolBatch] = useState(true);
    const [schoolBatchOptions, setSchoolBatchOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedSchoolBatch, setSelectedSchoolBatch] =
        useState<AppSelectOption | null>(null);

    const { handleSubmit, register, reset, control } =
        useForm<StudentFilterDataType>({
            defaultValues: filterData,
            mode: "onSubmit",
        });

    // handler
    const onSubmitHandler = (formData: StudentFilterDataType) => {
        const filterParams = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key] || formData[key] === false) {
                let nKey = key;
                switch (key) {
                    case "studentTrainer":
                    case "schoolBatch":
                        nKey = `${key}.id`;
                        break;
                }
                filterParams[nKey] = formData[key];
            }
        });
        setFilterData(filterParams);
    };

    const loadStudentTrainerOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            userType: USERTYPE.USERTYPE_PEDAGOGICAL_REFERENCE_TRAINER,
        };
        setLoadingStudentTrainers(true);
        EntityApi.getCollection<Entity>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setStudentTrainerOptions(
                        response?.items?.map((e) => ({
                            value: EntityApi.toResourceIRI(e.id),
                            label: e.fullName,
                        })) || []
                    );
                }
            })
            .finally(() => setLoadingStudentTrainers(false));
    };

    const loadSchoolBatchOptions = () => {
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };
        setLoadingSchoolBatch(true);
        SchoolBatchApi.getCollection<SchoolBatch>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setSchoolBatchOptions(
                        response?.items?.map((e) => ({
                            value: SchoolBatchApi.toResourceIRI(e.id),
                            label: e.name,
                        })) || []
                    );
                }
            })
            .finally(() => setLoadingSchoolBatch(false));
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedStudentTrainer(null);
        setSelectedSchoolBatch(null);
        setSelectedNationality(null);
        setSelectedStatus(null);
        reset(defaultValues);
    };

    // useEffect
    useEffect(() => {
        if (isInit) {
            setStudentTrainerOptions([]);
            setSelectedStudentTrainer(null);
            setSchoolBatchOptions([]);
            setSelectedSchoolBatch(null);
            if (
                grant.isInternshipCoordinatorTrainer ||
                grant.isSchoolAndCompany
            ) {
                loadStudentTrainerOptions();
            }
            loadSchoolBatchOptions();
        }
    }, [isInit]);

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            setIsInit={setIsInit}
            zIndex={"1400"}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={4} alignItems={"stretch"} justify={"start"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"user_search"}
                            defaultValue={filterData?.user_search || ""}
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    <SimpleGrid
                        columns={{ sm: 1, md: 2 }}
                        spacingX={5}
                        spacingY={4}
                        w={"full"}
                    >
                        {(grant.isInternshipCoordinatorTrainer ||
                            grant.isSchoolAndCompany) && (
                            <AppFormControl
                                label={t("ent.User:studentTrainer.label")}
                            >
                                <Controller
                                    control={control}
                                    name="studentTrainer"
                                    render={({ field }) => (
                                        <AppSelect<AppSelectOption>
                                            name={field.name}
                                            isClearable
                                            isLoading={loadingStudentTrainers}
                                            options={studentTrainerOptions}
                                            value={
                                                selectedStudentTrainer || null
                                            }
                                            placeholder={t(
                                                "ent.User:studentTrainer.placeholder"
                                            )}
                                            onChange={(e) => {
                                                field.onChange(
                                                    e?.value || null
                                                );
                                                setSelectedStudentTrainer(
                                                    e as AppSelectOption
                                                );
                                            }}
                                        />
                                    )}
                                />
                            </AppFormControl>
                        )}
                        <AppFormControl label={t("ent.User:nationality.label")}>
                            <Controller
                                control={control}
                                name="nationality"
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isClearable
                                        options={Object.values(
                                            APPC.BE.User.NATIONALITY
                                        ).map((nat) => ({
                                            value: nat,
                                            label: nat,
                                        }))}
                                        value={selectedNationality}
                                        placeholder={t(
                                            "ent.User:nationality.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(e?.value || null);
                                            setSelectedNationality(
                                                e as AppSelectOption
                                            );
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                        <AppFormControl label={t("ent.User:status.label")}>
                            <Controller
                                control={control}
                                name="status"
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isClearable
                                        options={Object.values(
                                            APPC.BE.User.STUDENT_STATUS
                                        ).map((s) => ({
                                            value: s,
                                            label: t(
                                                `con.User:STUDENT_STATUS_${s}`
                                            ),
                                        }))}
                                        value={selectedStatus}
                                        placeholder={t(
                                            "ent.User:status.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(e?.value || null);
                                            setSelectedStatus(
                                                e as AppSelectOption
                                            );
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                        <AppFormControl label={t("ent.User:schoolBatch.label")}>
                            <Controller
                                control={control}
                                name="schoolBatch"
                                render={({ field }) => (
                                    <AppSelect<AppSelectOption>
                                        name={field.name}
                                        isClearable
                                        isLoading={loadingSchoolBatch}
                                        options={schoolBatchOptions}
                                        value={selectedSchoolBatch || null}
                                        placeholder={t(
                                            "ent.User:schoolBatch.placeholder"
                                        )}
                                        onChange={(e) => {
                                            field.onChange(e?.value || null);
                                            setSelectedSchoolBatch(
                                                e as AppSelectOption
                                            );
                                        }}
                                    />
                                )}
                            />
                        </AppFormControl>
                    </SimpleGrid>
                    <AppFormControl label={t("ent.User:isActive.label")}>
                        <RadioGroup
                            defaultValue={filterData?.isActive}
                            w={"full"}
                        >
                            <HStack alignItems={"start"} w={"full"}>
                                <Radio {...register("isActive")} value={""}>
                                    {t("cmn:label.none")}
                                </Radio>
                                <Radio {...register("isActive")} value={"true"}>
                                    {t("cmn:label.yes")}
                                </Radio>
                                <Radio
                                    {...register("isActive")}
                                    value={"false"}
                                >
                                    {t("cmn:label.no")}
                                </Radio>
                            </HStack>
                        </RadioGroup>
                    </AppFormControl>
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
